import axios from 'axios';
import { environment } from '../../environments/environment';

/**
 * Reads behind the case-workflow dashboard.
 *
 * Follows benchmarkService: one private `get`, the control plane's
 * `{ status, data }` envelope unwrapped in one place, and `null` for anything
 * the platform did not measure rather than a plausible default.
 */

const getBaseUrl = () => `${environment.controlplaneUrl}/api/v1/case-workflows`;

const getHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
};

/* ---------------------------------------------------------------- contract */

/**
 * A dimension the numbers may be broken down by.
 *
 * Comes from the platform contract, never from a list in a component. Three of
 * these fields exist to prevent a specific misreading and the UI may not ignore
 * any of them — see the comments on each.
 */
export interface CaseWorkflowDimension {
  code: string;
  label: string;
  source: 'event' | 'record';
  column?: string;
  path?: string;
  kind?: 'jsonb_array' | 'json_path';
  /** Absent values render as this named bucket — never as 0, never dropped. */
  nullBucket?: string;
  /** Bars sum to more than the case total; the chart has to say so. */
  multiValued?: boolean;
  /** Record types this can describe. Others are structurally empty, not zero. */
  availableOn?: string[];
  note?: string;
  values?: string[];
}

export interface CaseWorkflowState {
  code: string;
  label: string;
  ordinal: number;
  terminal: boolean;
  description: string;
}

export interface CaseWorkflowContract {
  contractVersion: string;
  workflowCode: string;
  states: CaseWorkflowState[];
  dimensions: CaseWorkflowDimension[];
  recordTypes: string[];
  triggers: string[];
  reservedTriggers: string[];
  rules: { code: string; severity: 'reject' | 'warn'; message: string }[];
  defaultTransitions: DeclaredTransition[];
  editableFields: Record<string, string[]>;
  /** The human decisions a case passes, in pipeline order (contract 1.4.0). */
  reviewGates?: ReviewGate[];
  /** Work queued for each next flow (contract 1.4.0). */
  workQueues?: WorkQueue[];
  /** Declared non-case dependencies between flows (contract 1.5.0). */
  feeds?: FlowFeed[];
  /** The flow graph the queues, gates and feeds imply (contract 1.5.0). */
  flowEdges?: FlowEdge[];
}

export interface FlowFeed {
  from: string;
  to: string;
  via: string;
  label: string;
}

/** One arrow between two flows, over base flow codes. */
export interface FlowEdge {
  from: string;
  to: string;
  /** A case hand-off through a work queue, or a declared data feed. */
  kind: 'handoff' | 'feed';
  /** Work queues counted on this arrow (codes into /actions' queues). */
  queues: string[];
  /** Review gates on this arrow (codes into /actions' gates). */
  gates: string[];
  /** Feeds only: what passes along the arrow, and how to say it. */
  via?: string | null;
  label?: string | null;
}

/** One of the project's workflows, anchored on its newest campaign. */
export interface PipelineNode {
  campaignId: string;
  campaignName: string | null;
  campaignType: string | null;
  campaignTypeName: string | null;
  /** The type as a grouping key (hand-typed variants folded together). */
  flowType: string | null;
  /** Base flow code; null only on an unconnected workflow whose flow is unknown. */
  flowCode: string | null;
  /** Older campaigns on the same flow. */
  alsoCampaignIds?: string[];
}

export interface PipelineResponse {
  projectId: string;
  nodes: PipelineNode[];
  edges: FlowEdge[];
  /** Workflows in no hand-off or feed. */
  unconnected: PipelineNode[];
}

export interface ReviewGateOption {
  code: string;
  label: string;
  /** Sign-off only: whether this disposition takes the case on to SAR preparation. */
  refersSar?: boolean;
}

export interface ReviewGateOutcome {
  code: string;
  label: string;
  when: { op: string; value: unknown };
  leadsTo: { transition?: string; queue?: string };
}

export interface ReviewGate {
  code: string;
  label: string;
  reviewKind: string;
  raisingFlow: string;
  question?: string;
  /** Null for triage: the tier set is the org's. */
  options: ReviewGateOption[] | null;
  outcomes: ReviewGateOutcome[];
}

export interface WorkQueue {
  code: string;
  label: string;
  recordType: string;
  workflowState: string;
  status: string;
  consumerFlow: string | null;
  awaitsGate?: string;
  excludePriorities?: string[];
}

export interface DeclaredTransition {
  code: string;
  declares: { from?: string; to: string; label: string };
  observes: {
    flowTemplateCode: string;
    recordType: string;
    trigger: string;
    condition?: unknown;
    precededBy?: string;
  };
}

/* ------------------------------------------------------------- dashboard */

export interface FunnelEdge {
  from: string;
  to: string;
  cases: number;
  /** Elapsed wall time between two steps, not analyst working time. */
  p50Hours: number | null;
  p90Hours: number | null;
}

export interface FunnelStage {
  transitionCode: string;
  /** Rows. At the alert stage this is alerts, not cases. */
  transitions: number;
  /** Distinct case ids — a case is a customer under investigation. */
  cases: number;
}

export interface FunnelResponse {
  edges: FunnelEdge[];
  stages: FunnelStage[];
  /** 0-1. An org with customer-managed records carries no case ids at all. */
  caseIdCoverage: number | null;
  events: number | null;
  /**
   * The workflow versions this window spans.
   *
   * More than one means the declaration was edited mid-window and the dwell
   * figures average across two different processes — so the break is shown
   * rather than the average.
   */
  workflowVersions: string[];
}

export interface BreakdownRow {
  dimensionValue: string | null;
  bucket: string | null;
  transitions: number | null;
  cases: number | null;
}

/**
 * Which cases a read covered.
 *
 * `not_closed` is defined at case grain on the platform — a case stays in this
 * population while any record under it sits in a non-terminal state — so it is
 * never re-derived here from a state list.
 */
export type CaseStatus = 'all' | 'not_closed';

export interface BreakdownResponse {
  dimension: string;
  label: string;
  level: 'case' | 'record';
  valueAt: 'event' | 'case_latest';
  /** Echoed by the platform, so the chart can name its own population. */
  caseStatus: CaseStatus;
  bucket: 'day' | 'week' | 'month' | null;
  tz: string | null;
  multiValued: boolean;
  nullBucket: string | null;
  availableOn: string[] | null;
  note: string | null;
  /** The complete axis, so a period with no activity renders as empty. */
  buckets: string[] | null;
  rows: BreakdownRow[];
}

export interface CensusState {
  state: string;
  /** Rows. At case grain, every record under the cases counted in this state. */
  records: number;
  /** Case grain only: distinct cases, each counted in exactly one state. */
  cases?: number;
  oldestAt: string | null;
  p50AgeHours: number | null;
  p90AgeHours: number | null;
}

export interface CensusResponse {
  asOf?: string;
  /**
   * 'case' counts each case once, in its most advanced open state (Closed when
   * every record is terminal). 'record' counts rows in case_records — a case with
   * 141 alerts counts 141 times.
   */
  grain: 'record' | 'case';
  /** Case grain: the per-state counts sum to this. */
  totalCases?: number;
  states?: CensusState[];
  bucket?: 'day' | 'week' | 'month';
  tz?: string;
  /** Buckets before this under-report: the series is rebuilt from the log. */
  reconstructedSince?: string | null;
  /**
   * Reconstructed records that no longer exist in the case store.
   *
   * The event log is append-only and outlives the rows it describes: a flow
   * re-run writes a fresh set of record keys and the previous set can be
   * removed, while their events remain. Non-zero means this series legitimately
   * exceeds the "now" census, and the chart has to say so — otherwise toggling
   * between the two views shows a drop that never happened.
   */
  recordsNoLongerPresent?: number;
  buckets?: { at: string; state: string; records?: number; cases?: number }[];
}

export interface DiffResponse {
  scopeRung: string;
  declaredNotObserved: string[];
  observedNotDeclared: string[];
  agreeing: string[];
  unattributed: boolean;
  warnings: { rule: string; severity: string; path: string; message: string }[];
}

export interface DeclaredGraphResponse {
  workflowCode: string;
  workflowVersion: number | null;
  source: 'document' | 'default';
  scopeRung: 'project' | 'org' | 'global' | 'builtin';
  editable: boolean;
  nodes: { id: string; data: Record<string, any> }[];
  edges: { id: string; source: string | null; target: string; data: Record<string, any> }[];
}

export interface CaseRow {
  caseId: string | null;
  recordType: string;
  recordKey: string;
  customerId: string | null;
  customerName: string | null;
  priority: string | null;
  machinePriority: string | null;
  prioritySource: string | null;
  alertTypes: string[];
  workflowState: string | null;
  workflowStateAt: string | null;
  transitionCode: string | null;
  transitionPriority: string | null;
  matchedAt: string | null;
}

export interface CaseListResponse {
  pagination: { currentPage: number; pageSize: number; totalRecords: number; totalPages: number };
  /**
   * Distinct cases behind the same rows.
   *
   * The list is keyed on (record_type, record_key), but half the figures that
   * open it are case-grained — "Cases opened: 39" lands on 1,746 alert records.
   * Carrying both is what lets the table state the relationship instead of
   * appearing to contradict the card that was clicked.
   */
  totalCases: number | null;
  caseIdCoverage: number | null;
  cases: CaseRow[];
}

export interface TimelineStep {
  transitionCode: string | null;
  state: string;
  priorState: string | null;
  workflowVersion: number | null;
  priority: string | null;
  prioritySource: string | null;
  flowCode: string | null;
  runId: string | null;
  recordType: string;
  recordKeys: string[];
  /** Consecutive same-code events, collapsed. Expandable on demand. */
  count: number;
  firstAt: string;
  lastAt: string;
}

export interface TimelineResponse {
  pagination: { currentPage: number; pageSize: number; totalRecords: number; totalPages: number };
  events: number;
  steps: TimelineStep[];
}

export interface FilterOptionsResponse {
  /** Root flows only — sub-graphs inherit their parent's code. */
  flows: { flowCode: string; instances: string[]; transitions: number; cases: number }[];
  runs: { runId: string; transitions: number; firstAt: string; lastAt: string }[];
}

/**
 * The filter set every endpoint shares.
 *
 * snake_case because these are query parameters, matching benchmarkService's
 * ScoredAlertsQuery. One object, passed unchanged to every request, is what
 * makes a drill-down land on the same rows as the number it came from.
 */
export interface CaseWorkflowQuery {
  project_id?: string;
  since?: string;
  until?: string;
  flow_code?: string;
  run_id?: string;
  record_type?: string;
  /** With record_type, addresses a record that carries no case id. */
  record_key?: string;
  transition_code?: string;
  /** Current state on the record — how a census bar drills down. */
  workflow_state?: string;
  /**
   * The population, at case grain. Omitted means the whole one.
   *
   * Distinct from workflow_state, which is a single current state on a single
   * record: a case with 140 auto-closed alerts and one row still at L2 is
   * `not_closed`, and every one of those 140 alerts is in this population.
   */
  case_status?: CaseStatus;
  dimension?: string;
  value?: string;
  level?: 'case' | 'record';
  value_at?: 'event' | 'case_latest';
  bucket?: 'day' | 'week' | 'month';
  tz?: string;
  page?: number;
  page_size?: number;
}

/* --------------------------------------------------------------- plumbing */

/** Turns an axios rejection into something a panel can show a person. */
export class CaseWorkflowRequestError extends Error {
  readonly status: number | null;
  readonly violations: { field: string; message: string }[];

  constructor(message: string, status: number | null, violations: { field: string; message: string }[] = []) {
    super(message);
    this.name = 'CaseWorkflowRequestError';
    this.status = status;
    this.violations = violations;
  }
}

/* ---------------------------------------------------------------- actions */

/** One case waiting on a person at a gate, by its newest review thread. */
export interface DecisionItem {
  threadId: string;
  caseId: string | null;
  customerId: string;
  customerName: string | null;
  priority: string | null;
  suggestion: string | null;
  suggestionLabel: string | null;
  refersSar: boolean;
  rationale: string | null;
  precedentDivergence: boolean;
  createdAt: string;
  ageHours: number | null;
  /** Pending threads behind this case — more than one only for re-runs before supersession. */
  threads: number;
}

export interface GateActions {
  code: string;
  label: string;
  reviewKind: string;
  question: string | null;
  raisingFlow: string;
  campaignId: string | null;
  campaignName: string | null;
  openCases: number;
  openThreads: number;
  oldestAt: string | null;
  p50AgeHours: number | null;
  bySuggestion: Record<string, number>;
  /** Sign-off only: open cases whose suggested disposition refers for SAR. */
  sarRecommended: number | null;
  items: DecisionItem[];
  truncated: boolean;
}

export interface QueueActions {
  code: string;
  label: string;
  recordType: string;
  workflowState: string;
  status: string;
  consumerFlow: string | null;
  awaitsGate: string | null;
  campaignId: string | null;
  campaignName: string | null;
  cases: number;
  records: number;
  oldestAt: string | null;
  byPriority: Record<string, number>;
}

export interface ActionWarning {
  code: 'DRAFT_QUEUED_WHILE_EDD_OPEN' | 'SIGN_OFF_WITHOUT_EDD_RECORD' | 'EDD_WITHOUT_CASE' | string;
  count: number;
  sample: string[];
}

export interface ActionsResponse {
  asOf: string;
  projectId: string;
  gates: GateActions[];
  queues: QueueActions[];
  warnings: ActionWarning[];
}

export interface OutcomeTransition {
  code: string;
  label: string;
  recordType: string;
  cases: number;
  events: number;
}

export interface GateOutcomes {
  code: string;
  label: string;
  reviewKind: string;
  total: number;
  approved: number;
  overridden: number;
  autoApproved: number;
  outcomes: { code: string; label: string; leadsTo: ReviewGateOutcome['leadsTo']; count: number }[];
}

export interface OutcomesResponse {
  window: { since: string; until: string };
  projectId: string;
  transitions: OutcomeTransition[];
  gates: GateOutcomes[];
}

/** Where the insight card that decides a review thread lives. */
export interface ReviewCard {
  insightId: string;
  campaignId: string | null;
  capsuleId: string | null;
  groupIdentifier: string | null;
  groupStatus: string | null;
}

function toRequestError(error: any): CaseWorkflowRequestError {
  const status = error?.response?.status ?? null;
  if (status === 401 || status === 403) {
    return new CaseWorkflowRequestError('Your session has expired. Sign in again to view case activity.', status);
  }
  if (status === 422) {
    const violations = error?.response?.data?.violations ?? [];
    return new CaseWorkflowRequestError(
      violations[0]?.message || 'That view is not available.',
      status,
      violations
    );
  }
  const serverMessage = error?.response?.data?.error;
  return new CaseWorkflowRequestError(
    serverMessage || 'Could not load case activity. Check your connection and try again.',
    status
  );
}

async function get<T>(path: string, params?: Record<string, any>): Promise<T> {
  try {
    const res = await axios.get(`${getBaseUrl()}${path}`, { headers: getHeaders(), params });
    return res.data?.data as T;
  } catch (error) {
    throw toRequestError(error);
  }
}

/**
 * The contract, fetched once per session.
 *
 * Every picker, caption and bucket name on the dashboard is built from this. It
 * is memoised rather than re-fetched because a component that re-reads it per
 * render is a component that will eventually cache a stale copy of its own.
 */
let contractPromise: Promise<CaseWorkflowContract> | null = null;

export const caseWorkflowService = {
  getContract(): Promise<CaseWorkflowContract> {
    if (!contractPromise) {
      contractPromise = get<CaseWorkflowContract>('/contract').catch((error) => {
        // A failed fetch must not be cached, or the dashboard stays broken for
        // the rest of the session over one dropped request.
        contractPromise = null;
        throw error;
      });
    }
    return contractPromise;
  },

  getDeclaredGraph(params: CaseWorkflowQuery = {}) {
    return get<DeclaredGraphResponse>('/graph/declared', params);
  },

  getFunnel(params: CaseWorkflowQuery = {}) {
    return get<FunnelResponse>('/graph/funnel', params);
  },

  getCensus(params: CaseWorkflowQuery = {}) {
    return get<CensusResponse>('/graph/census', params);
  },

  getBreakdown(params: CaseWorkflowQuery) {
    return get<BreakdownResponse>('/graph/breakdown', params);
  },

  getDiff(params: CaseWorkflowQuery = {}) {
    return get<DiffResponse>('/graph/diff', params);
  },

  getFilterOptions(params: CaseWorkflowQuery = {}) {
    return get<FilterOptionsResponse>('/filters', params);
  },

  getCases(params: CaseWorkflowQuery = {}) {
    return get<CaseListResponse>('/cases', params);
  },

  /**
   * One case's whole path. A record with no case id is addressed by its path
   * instead, because case_id is an optional contract column.
   */
  getCaseTimeline(caseId: string | null, params: CaseWorkflowQuery = {}) {
    return get<TimelineResponse>(`/cases/${encodeURIComponent(caseId || '_')}/events`, params);
  },

  /** Decisions waiting per gate, work queued for the next flow, and hand-off warnings. */
  getActions(projectId: string, limit?: number) {
    return get<ActionsResponse>('/actions', { project_id: projectId, ...(limit ? { limit } : {}) });
  },

  /** The project's workflows and the hand-offs, gates and feeds between them. */
  getPipeline(projectId: string) {
    return get<PipelineResponse>('/pipeline', { project_id: projectId });
  },

  /** What a window's decisions decided, and the transitions cases made. */
  getOutcomes(projectId: string, since?: string, until?: string) {
    return get<OutcomesResponse>('/outcomes', {
      project_id: projectId, ...(since ? { since } : {}), ...(until ? { until } : {})
    });
  },

  /** The insight card a review thread is decided on, so the page can open it. */
  async getReviewCard(threadId: string): Promise<ReviewCard> {
    try {
      const res = await axios.get(
        `${environment.controlplaneUrl}/agent/flow/review/${encodeURIComponent(threadId)}/card`,
        { headers: getHeaders() }
      );
      return res.data?.data?.card as ReviewCard;
    } catch (error) {
      throw toRequestError(error);
    }
  }
};
